import React, { useState } from 'react';
import StepShell from './StepShell';
import FieldStack from './FieldStack';
import { SelectField } from '../../../ui/select-field';
import { InputField } from '../../../ui/input-field';
import { Input } from '../../../ui/input';
import { Button } from '../../../ui/button';
import { Badge } from '../../../ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../../../ui/alert';
import {
  TRANSPORT_MODE_OPTIONS,
  CAR_SIZE_OPTIONS,
  CAR_FUEL_OPTIONS,
} from '../../fixtures';
import { Plus, Trash2, ArrowDown, Car, CheckCircle2, Search } from 'lucide-react';

const LegEditor = ({ leg, index, onChange, onRemove }) => {
  const needsCar = leg.mode === 'car';
  const [plate, setPlate] = useState(leg.plate || '');
  const [lookupState, setLookupState] = useState('idle');

  const doLookup = () => {
    if (!plate) return;
    setLookupState('loading');
    setTimeout(() => {
      setLookupState('done');
      onChange({ ...leg, plate, carSize: 'medium', carFuel: 'petrol' });
    }, 900);
  };

  return (
    <div className='rounded-xl border-2 border-border p-3.5 relative'>
      <div className='flex items-center justify-between mb-3'>
        <Badge variant='outline' size='sm'>
          Leg {index + 1}
        </Badge>
        {index > 0 && (
          <Button variant='ghost' size='icon' onClick={onRemove} title='Remove leg'>
            <Trash2 size={14} strokeWidth={1.5} className='text-destructive/70' />
          </Button>
        )}
      </div>

      <FieldStack label='Mode of transport' required>
        <SelectField
          options={TRANSPORT_MODE_OPTIONS}
          value={leg.mode}
          onChange={(v) => onChange({ ...leg, mode: v })}
          placeholder='Pick a mode...'
          className='mb-0'
        />
      </FieldStack>

      <FieldStack label='One-way distance (km)' required>
        <InputField
          type='number'
          value={leg.distance}
          onChange={(e) => onChange({ ...leg, distance: Number(e.target.value) })}
          placeholder='e.g. 18.4'
          step='0.1'
          className='mb-0'
        />
      </FieldStack>

      {needsCar && (
        <>
          <FieldStack
            label='Licence plate (optional)'
            hint="We'll look up your car's size and fuel type automatically">
            <div className='flex gap-2'>
              <Input
                value={plate}
                onChange={(e) => setPlate(e.target.value.toUpperCase())}
                placeholder='e.g. AB12 CDE'
                className='flex-1 tracking-widest text-center font-mono'
              />
              <Button variant='outline' onClick={doLookup} loading={lookupState === 'loading'} disabled={!plate}>
                <Search size={14} strokeWidth={1.5} />
                Lookup
              </Button>
            </div>
            {lookupState === 'done' && (
              <div className='flex items-center gap-1.5 text-xs text-success mt-2'>
                <CheckCircle2 size={12} strokeWidth={1.5} />
                Matched: Medium Petrol. You can edit below.
              </div>
            )}
          </FieldStack>

          <FieldStack label='Car size' required>
            <SelectField
              options={CAR_SIZE_OPTIONS}
              value={leg.carSize || ''}
              onChange={(v) => onChange({ ...leg, carSize: v })}
              placeholder='Pick a size...'
              className='mb-0'
            />
          </FieldStack>

          <FieldStack label='Fuel type' required>
            <SelectField
              options={CAR_FUEL_OPTIONS}
              value={leg.carFuel || ''}
              onChange={(v) => onChange({ ...leg, carFuel: v })}
              placeholder='Pick a fuel type...'
              className='mb-0'
            />
          </FieldStack>
        </>
      )}
    </div>
  );
};

const Commuting = () => {
  const [commuteDays, setCommuteDays] = useState(2);
  const [legs, setLegs] = useState([
    { id: 1, mode: 'walk', distance: 0.6 },
    { id: 2, mode: 'train', distance: 18.4 },
  ]);

  const updateLeg = (id, updated) => setLegs((prev) => prev.map((l) => (l.id === id ? { ...updated, id } : l)));
  const addLeg = () => setLegs((prev) => [...prev, { id: Date.now(), mode: '', distance: '' }]);
  const removeLeg = (id) => setLegs((prev) => prev.filter((l) => l.id !== id));

  return (
    <StepShell
      title='Your commute'
      description='Your typical journey to the office. Add each leg — e.g. walk, then train, then walk.'>
      <FieldStack label='How many days per week do you commute to the office?' required>
        <InputField
          type='number'
          value={commuteDays}
          onChange={(e) => setCommuteDays(Number(e.target.value))}
          min={0}
          max={7}
          className='mb-0'
        />
      </FieldStack>

      {commuteDays === 0 ? (
        <Alert>
          <Car size={14} strokeWidth={1.5} />
          <AlertTitle>No commute this period</AlertTitle>
          <AlertDescription>
            You're 100% WFH — we'll skip the leg questions and wrap up at the review step.
          </AlertDescription>
        </Alert>
      ) : (
        <>
          <div className='space-y-3'>
            {legs.map((leg, index) => (
              <React.Fragment key={leg.id}>
                <LegEditor
                  leg={leg}
                  index={index}
                  onChange={(updated) => updateLeg(leg.id, updated)}
                  onRemove={() => removeLeg(leg.id)}
                />
                {index < legs.length - 1 && (
                  <div className='flex items-center justify-center'>
                    <div className='w-7 h-7 rounded-full bg-muted flex items-center justify-center text-muted-foreground'>
                      <ArrowDown size={14} strokeWidth={1.5} />
                    </div>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          {legs.length < 4 ? (
            <Button variant='outline' className='w-full' onClick={addLeg}>
              <Plus size={14} strokeWidth={1.5} />
              Add another leg ({legs.length} of 4)
            </Button>
          ) : (
            <div className='text-center text-xs text-muted-foreground'>
              Maximum 4 legs — covers most multi-modal commutes.
            </div>
          )}
        </>
      )}
    </StepShell>
  );
};

export default Commuting;
